import { useState, useEffect } from "react";
import { statsApi, usersApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, BarChart3, Edit, Upload, FileSpreadsheet, Download, Mail, Send } from "lucide-react";
import * as XLSX from "xlsx";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const StatsEntry = () => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [allStats, setAllStats] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [importData, setImportData] = useState([]);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [importMonth, setImportMonth] = useState(new Date().getMonth() + 1);
  
  // Mass email state
  const [massEmailDialogOpen, setMassEmailDialogOpen] = useState(false);
  const [sendingMassEmail, setSendingMassEmail] = useState(false);
  const [massEmailData, setMassEmailData] = useState({
    month: MONTHS[new Date().getMonth()]?.label || "January",
    year: new Date().getFullYear(),
    customMessage: ""
  });

  const currentYear = new Date().getFullYear();
  const [filters, setFilters] = useState({
    year: currentYear,
    month: "",
    employee: "",
  });

  const [formData, setFormData] = useState({
    user_id: "",
    year: currentYear,
    month: new Date().getMonth() + 1,
    billable_hours: "",
    total_hours: "",
    plt_hours: "",
    billed_amount: "",
    realized_amount: "",
    total_pay: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [employeesRes, statsRes] = await Promise.all([
        usersApi.getEmployees(),
        statsApi.getAll(),
      ]);
      // Only show employees who track stats
      setEmployees(employeesRes.data.filter(emp => emp.tracks_stats !== false));
      setAllStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        billable_hours: parseFloat(formData.billable_hours) || 0,
        total_hours: parseFloat(formData.total_hours) || 0,
        plt_hours: parseFloat(formData.plt_hours) || 0,
        billed_amount: parseFloat(formData.billed_amount) || 0,
        realized_amount: parseFloat(formData.realized_amount) || 0,
        total_pay: parseFloat(formData.total_pay) || 0,
      };

      if (editMode && editingId) {
        await statsApi.update(editingId, payload);
        toast.success("Stats updated successfully");
      } else {
        await statsApi.create(payload);
        toast.success("Stats added successfully");
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save stats");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (stat) => {
    setFormData({
      user_id: stat.user_id,
      year: stat.year,
      month: stat.month,
      billable_hours: stat.billable_hours.toString(),
      total_hours: stat.total_hours.toString(),
      plt_hours: (stat.plt_hours || 0).toString(),
      billed_amount: stat.billed_amount.toString(),
      realized_amount: stat.realized_amount.toString(),
      total_pay: stat.total_pay.toString(),
    });
    setEditMode(true);
    setEditingId(stat.id);
    setDialogOpen(true);
  };

  const handleDelete = async (statId) => {
    try {
      await statsApi.delete(statId);
      toast.success("Stats deleted successfully");
      loadData();
    } catch (error) {
      toast.error("Failed to delete stats");
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      year: currentYear,
      month: new Date().getMonth() + 1,
      billable_hours: "",
      total_hours: "",
      plt_hours: "",
      billed_amount: "",
      realized_amount: "",
      total_pay: "",
    });
    setEditMode(false);
    setEditingId(null);
  };

  const getEmployeeName = (userId) => {
    const emp = employees.find((e) => e.id === userId);
    return emp?.name || "Unknown";
  };

  const findEmployeeByName = (name) => {
    if (!name) return null;
    const normalizedName = name.toString().toLowerCase().trim();
    return employees.find((emp) => {
      const empName = emp.name.toLowerCase().trim();
      // Try exact match first
      if (empName === normalizedName) return true;
      // Try first name match
      const firstName = empName.split(" ")[0];
      if (firstName === normalizedName) return true;
      // Try last name match
      const lastName = empName.split(" ").pop();
      if (lastName === normalizedName) return true;
      // Try partial match
      if (empName.includes(normalizedName) || normalizedName.includes(empName)) return true;
      return false;
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        // Map Excel columns to our fields
        const mappedData = jsonData.map((row, index) => {
          // Try to find the employee column (flexible naming)
          const employeeName = row["Employee"] || row["employee"] || row["Name"] || row["name"] || 
                              row["Employee Name"] || row["EmployeeName"] || Object.values(row)[0];
          const employee = findEmployeeByName(employeeName);
          
          return {
            rowNum: index + 2, // Excel row (1-indexed + header)
            employeeName: employeeName,
            employee: employee,
            user_id: employee?.id || null,
            billable_hours: parseFloat(row["Billable Hours"] || row["billable_hours"] || row["Billable"] || 0) || 0,
            total_hours: parseFloat(row["Total Hours"] || row["total_hours"] || row["Total"] || 0) || 0,
            plt_hours: parseFloat(row["PLT+Holiday"] || row["PLT + Holiday"] || row["plt_hours"] || row["PLT"] || row["Holiday"] || 0) || 0,
            billed_amount: parseFloat(row["Produced"] || row["produced"] || row["Billed Amount"] || row["billed_amount"] || 0) || 0,
            realized_amount: parseFloat(row["Billed"] || row["billed"] || row["Realized"] || row["realized_amount"] || 0) || 0,
            total_pay: parseFloat(row["Total Pay"] || row["total_pay"] || row["Pay"] || 0) || 0,
            valid: !!employee,
          };
        });

        setImportData(mappedData);
        
        const validCount = mappedData.filter(d => d.valid).length;
        const invalidCount = mappedData.filter(d => !d.valid).length;
        
        if (invalidCount > 0) {
          toast.warning(`${validCount} rows matched, ${invalidCount} rows have unmatched employees`);
        } else {
          toast.success(`${validCount} rows ready to import`);
        }
      } catch (error) {
        console.error("Excel parse error:", error);
        toast.error("Failed to parse Excel file. Please check the format.");
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    const validData = importData.filter(d => d.valid);
    if (validData.length === 0) {
      toast.error("No valid rows to import");
      return;
    }

    setImporting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const row of validData) {
      try {
        await statsApi.create({
          user_id: row.user_id,
          year: importYear,
          month: importMonth,
          billable_hours: row.billable_hours,
          total_hours: row.total_hours,
          plt_hours: row.plt_hours,
          billed_amount: row.billed_amount,
          realized_amount: row.realized_amount,
          total_pay: row.total_pay,
        });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to import row for ${row.employeeName}:`, error);
      }
    }

    setImporting(false);
    setImportDialogOpen(false);
    setImportData([]);
    loadData();

    if (errorCount > 0) {
      toast.warning(`Imported ${successCount} rows, ${errorCount} failed (may already exist)`);
    } else {
      toast.success(`Successfully imported ${successCount} stats entries`);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        "Employee": "John Smith",
        "Billable Hours": 120,
        "Total Hours": 160,
        "PLT+Holiday": 8,
        "Produced": 15000,
        "Billed": 14000,
        "Total Pay": 8000,
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stats Template");
    XLSX.writeFile(wb, "stats_import_template.xlsx");
  };

  const handleSendMassEmail = async () => {
    setSendingMassEmail(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API_URL}/api/notifications/mass-stats-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          month: massEmailData.month,
          year: massEmailData.year,
          custom_message: massEmailData.customMessage || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Emails sent to ${data.sent} employees!`);
        if (data.failed > 0) {
          toast.warning(`${data.failed} emails failed to send`);
        }
        setMassEmailDialogOpen(false);
      } else {
        toast.error(data.detail || "Failed to send emails");
      }
    } catch (error) {
      toast.error("Failed to send mass email");
      console.error(error);
    } finally {
      setSendingMassEmail(false);
    }
  };

  const filteredStats = allStats.filter((stat) => {
    if (filters.year && stat.year !== parseInt(filters.year)) return false;
    if (filters.month && filters.month !== "all" && stat.month !== parseInt(filters.month)) return false;
    if (filters.employee && filters.employee !== "all" && stat.user_id !== filters.employee) return false;
    return true;
  });

  // Calculate totals by employee for the filtered stats
  const employeeTotals = filteredStats.reduce((acc, stat) => {
    const empId = stat.user_id;
    if (!acc[empId]) {
      acc[empId] = {
        user_id: empId,
        name: getEmployeeName(empId),
        billable_hours: 0,
        total_hours: 0,
        plt_hours: 0,
        billed_amount: 0,
        realized_amount: 0,
        total_pay: 0,
        count: 0,
      };
    }
    acc[empId].billable_hours += parseFloat(stat.billable_hours) || 0;
    acc[empId].total_hours += parseFloat(stat.total_hours) || 0;
    acc[empId].plt_hours += parseFloat(stat.plt_hours) || 0;
    acc[empId].billed_amount += parseFloat(stat.billed_amount) || 0;
    acc[empId].realized_amount += parseFloat(stat.realized_amount) || 0;
    acc[empId].total_pay += parseFloat(stat.total_pay) || 0;
    acc[empId].count += 1;
    return acc;
  }, {});

  const sortedEmployeeTotals = Object.values(employeeTotals).sort((a, b) => a.name.localeCompare(b.name));

  // Grand totals
  const grandTotals = sortedEmployeeTotals.reduce((acc, emp) => ({
    billable_hours: acc.billable_hours + emp.billable_hours,
    total_hours: acc.total_hours + emp.total_hours,
    plt_hours: acc.plt_hours + emp.plt_hours,
    billed_amount: acc.billed_amount + emp.billed_amount,
    realized_amount: acc.realized_amount + emp.realized_amount,
    total_pay: acc.total_pay + emp.total_pay,
  }), { billable_hours: 0, total_hours: 0, plt_hours: 0, billed_amount: 0, realized_amount: 0, total_pay: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stats-entry">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Stats Entry</h1>
          <p className="text-muted-foreground mt-1">
            Enter and manage employee performance statistics
          </p>
        </div>
        <div className="flex gap-2">
          {/* Mass Email Button */}
          <Dialog open={massEmailDialogOpen} onOpenChange={setMassEmailDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2 border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10" data-testid="mass-email-btn">
                <Mail size={18} />
                Notify All
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="mass-email-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-emerald-500" />
                  Send Stats Update Notification
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  Send an email to all employees notifying them that their stats have been updated.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select 
                      value={massEmailData.month} 
                      onValueChange={(v) => setMassEmailData(prev => ({ ...prev, month: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m) => (
                          <SelectItem key={m.value} value={m.label}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select 
                      value={String(massEmailData.year)} 
                      onValueChange={(v) => setMassEmailData(prev => ({ ...prev, year: parseInt(v) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Custom Message (Optional)</Label>
                  <Textarea
                    placeholder="Your performance stats have been updated in the Employee Portal."
                    value={massEmailData.customMessage}
                    onChange={(e) => setMassEmailData(prev => ({ ...prev, customMessage: e.target.value }))}
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use the default message.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMassEmailDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSendMassEmail} 
                  disabled={sendingMassEmail}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                >
                  {sendingMassEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send to All Employees
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Import Excel Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            setImportDialogOpen(open);
            if (!open) setImportData([]);
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-stats-btn">
                <Upload size={18} />
                Import Excel
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl" data-testid="import-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet size={20} />
                  Import Stats from Excel
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={importMonth.toString()} onValueChange={(v) => setImportMonth(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m) => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={importYear.toString()} onValueChange={(v) => setImportYear(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Excel File</Label>
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="cursor-pointer"
                    data-testid="import-file-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Columns: Employee, Billable Hours, Total Hours, PLT+Holiday, Produced, Billed, Total Pay
                  </p>
                </div>

                <Button variant="link" size="sm" onClick={downloadTemplate} className="gap-1 p-0 h-auto">
                  <Download size={14} />
                  Download Template
                </Button>

                {importData.length > 0 && (
                  <div className="border rounded-lg max-h-64 overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead className="text-right">Billable</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">PLT</TableHead>
                          <TableHead className="text-right">Produced</TableHead>
                          <TableHead className="text-right">Billed</TableHead>
                          <TableHead className="text-right">Pay</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importData.map((row, idx) => (
                          <TableRow key={idx} className={row.valid ? "" : "bg-rose-50"}>
                            <TableCell className="font-medium">
                              {row.valid ? row.employee?.name : (
                                <span className="text-rose-600">{row.employeeName} (not found)</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">{row.billable_hours}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{row.total_hours}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{row.plt_hours}</TableCell>
                            <TableCell className="text-right font-mono text-sm">${row.billed_amount.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono text-sm">${row.realized_amount.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-mono text-sm">${row.total_pay.toLocaleString()}</TableCell>
                            <TableCell>
                              {row.valid ? (
                                <span className="text-emerald-500 text-xs">✓ Ready</span>
                              ) : (
                                <span className="text-rose-600 text-xs">✗ No match</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="flex justify-between items-center pt-4">
                  <p className="text-sm text-muted-foreground">
                    {importData.filter(d => d.valid).length} of {importData.length} rows ready
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleImport}
                      disabled={importing || importData.filter(d => d.valid).length === 0}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        `Import ${importData.filter(d => d.valid).length} Rows`
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Stats Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="add-stats-btn">
                <Plus size={18} />
                Add Stats
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg" data-testid="stats-dialog">
              <DialogHeader>
                <DialogTitle>{editMode ? "Edit Stats" : "Add New Stats"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Employee {editMode && <span className="text-xs text-amber-500">(can reassign)</span>}</Label>
                    <Select
                      value={formData.user_id}
                      onValueChange={(value) =>
                        setFormData({ ...formData, user_id: value })
                      }
                    >
                      <SelectTrigger data-testid="select-employee">
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...employees].sort((a, b) => a.name.localeCompare(b.name)).map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select
                    value={formData.month.toString()}
                    onValueChange={(value) =>
                      setFormData({ ...formData, month: parseInt(value) })
                    }
                    disabled={editMode}
                  >
                    <SelectTrigger data-testid="select-month">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m) => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Year</Label>
                <Select
                  value={formData.year.toString()}
                  onValueChange={(value) =>
                    setFormData({ ...formData, year: parseInt(value) })
                  }
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="select-year">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billable_hours">Billable Hours</Label>
                  <Input
                    id="billable_hours"
                    type="number"
                    step="0.1"
                    value={formData.billable_hours}
                    onChange={(e) =>
                      setFormData({ ...formData, billable_hours: e.target.value })
                    }
                    placeholder="0.0"
                    required
                    data-testid="input-billable-hours"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_hours">Total Hours</Label>
                  <Input
                    id="total_hours"
                    type="number"
                    step="0.1"
                    value={formData.total_hours}
                    onChange={(e) =>
                      setFormData({ ...formData, total_hours: e.target.value })
                    }
                    placeholder="0.0"
                    required
                    data-testid="input-total-hours"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="plt_hours">PLT + Holiday Hours</Label>
                <Input
                  id="plt_hours"
                  type="number"
                  step="0.1"
                  value={formData.plt_hours}
                  onChange={(e) =>
                    setFormData({ ...formData, plt_hours: e.target.value })
                  }
                  placeholder="0.0"
                  data-testid="input-plt-hours"
                />
                <p className="text-xs text-muted-foreground">
                  Billable % = Billable Hours / (Total Hours - PLT - Holiday)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billed_amount">Produced ($)</Label>
                  <Input
                    id="billed_amount"
                    type="number"
                    step="0.01"
                    value={formData.billed_amount}
                    onChange={(e) =>
                      setFormData({ ...formData, billed_amount: e.target.value })
                    }
                    placeholder="0.00"
                    required
                    data-testid="input-billed-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="realized_amount">Billed ($)</Label>
                  <Input
                    id="realized_amount"
                    type="number"
                    step="0.01"
                    value={formData.realized_amount}
                    onChange={(e) =>
                      setFormData({ ...formData, realized_amount: e.target.value })
                    }
                    placeholder="0.00"
                    required
                    data-testid="input-realized-amount"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="total_pay">Total Pay ($)</Label>
                <Input
                  id="total_pay"
                  type="number"
                  step="0.01"
                  value={formData.total_pay}
                  onChange={(e) =>
                    setFormData({ ...formData, total_pay: e.target.value })
                  }
                  placeholder="0.00"
                  required
                  data-testid="input-total-pay"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || !formData.user_id}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-stats-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editMode ? (
                    "Update Stats"
                  ) : (
                    "Add Stats"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Year</Label>
              <Select
                value={filters.year.toString()}
                onValueChange={(value) =>
                  setFilters({ ...filters, year: parseInt(value) })
                }
              >
                <SelectTrigger data-testid="filter-year">
                  <SelectValue placeholder="All years" />
                </SelectTrigger>
                <SelectContent>
                  {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
                    <SelectItem key={y} value={y.toString()}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Month</Label>
              <Select
                value={filters.month}
                onValueChange={(value) =>
                  setFilters({ ...filters, month: value })
                }
              >
                <SelectTrigger data-testid="filter-month">
                  <SelectValue placeholder="All months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {MONTHS.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select
                value={filters.employee}
                onValueChange={(value) =>
                  setFilters({ ...filters, employee: value })
                }
              >
                <SelectTrigger data-testid="filter-employee">
                  <SelectValue placeholder="All employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {[...employees].sort((a, b) => a.name.localeCompare(b.name)).map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Totals Summary - shows when viewing all months or multiple records */}
      {(filters.month === "all" || !filters.month) && sortedEmployeeTotals.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <BarChart3 className="text-emerald-500" size={20} />
              <CardTitle>Employee Totals Summary ({filters.year})</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table data-testid="totals-table">
                <TableHeader>
                  <TableRow className="bg-muted">
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-center">Months</TableHead>
                    <TableHead className="text-right">Billable Hrs</TableHead>
                    <TableHead className="text-right">Total Hrs</TableHead>
                    <TableHead className="text-right">PLT + Holiday</TableHead>
                    <TableHead className="text-right">Produced ($)</TableHead>
                    <TableHead className="text-right">Billed ($)</TableHead>
                    <TableHead className="text-right">Pay ($)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedEmployeeTotals.map((emp) => (
                    <TableRow key={emp.user_id} className="hover:bg-muted">
                      <TableCell className="font-medium">{emp.name}</TableCell>
                      <TableCell className="text-center text-muted-foreground">{emp.count}</TableCell>
                      <TableCell className="text-right font-mono">{emp.billable_hours.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono">{emp.total_hours.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono">{emp.plt_hours.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono">${emp.billed_amount.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">${emp.realized_amount.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">${emp.total_pay.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {/* Grand Total Row */}
                  <TableRow className="bg-muted font-bold border-t-2 border-slate-300">
                    <TableCell>GRAND TOTAL</TableCell>
                    <TableCell className="text-center">{sortedEmployeeTotals.reduce((sum, e) => sum + e.count, 0)}</TableCell>
                    <TableCell className="text-right font-mono">{grandTotals.billable_hours.toFixed(1)}</TableCell>
                    <TableCell className="text-right font-mono">{grandTotals.total_hours.toFixed(1)}</TableCell>
                    <TableCell className="text-right font-mono">{grandTotals.plt_hours.toFixed(1)}</TableCell>
                    <TableCell className="text-right font-mono">${grandTotals.billed_amount.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">${grandTotals.realized_amount.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">${grandTotals.total_pay.toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats table */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BarChart3 className="text-blue-500" size={20} />
            <CardTitle>Statistics ({filteredStats.length} records)</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table data-testid="stats-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Billable Hrs</TableHead>
                  <TableHead className="text-right">Total Hrs</TableHead>
                  <TableHead className="text-right">PLT + Holiday</TableHead>
                  <TableHead className="text-right">Produced ($)</TableHead>
                  <TableHead className="text-right">Billed ($)</TableHead>
                  <TableHead className="text-right">Pay ($)</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStats.map((stat) => (
                  <TableRow key={stat.id} className="table-row-hover">
                    <TableCell className="font-medium">
                      {getEmployeeName(stat.user_id)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {MONTHS.find((m) => m.value === stat.month)?.label} {stat.year}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {stat.billable_hours.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {stat.total_hours.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {(stat.plt_hours || 0).toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${stat.billed_amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${stat.realized_amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${stat.total_pay.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(stat)}
                          className="text-muted-foreground hover:text-blue-500"
                          data-testid={`edit-stat-${stat.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-rose-600"
                              data-testid={`delete-stat-${stat.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Stats</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this stats entry?
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(stat.id)}
                                className="bg-rose-600 hover:bg-rose-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredStats.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No statistics found for the selected filters.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatsEntry;
